import { Avatar } from "antd";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserShort } from "tm-shared/types";

import FlexContainer from "@/components/FlexContainer";
import { useAppDispatch } from "@/hooks/redux";
import { clearSVActiveSchedule } from "@/store/slices/sv";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";

import { SIZES } from "../../constants";
import { useScheduleStatus } from "../../hooks/useScheduleStatus";
import {
    StyledMemberCard,
    StyledTeamBadge,
    StyledTeamLogo,
} from "../../Styled";
import { MemberActions } from "../MemberActions";
import { MemberInfo } from "../MemberInfo";

interface MemberCardProps {
    member: UserShort;
    routeId: string;
    isActive: boolean;
    isProcessing: boolean;
    onLoadSchedule: (
        userId: string,
        roleSlug: string,
        routeId?: string,
    ) => void;
    onNavigate: () => void;
}

export const MemberCard = ({
    member,
    isActive,
    isProcessing,
    onLoadSchedule,
    onNavigate,
    routeId,
}: MemberCardProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { hasSchedule, canAccess } = useScheduleStatus(
        member.userId,
        isActive,
    );

    const handleLoad = () => {
        onLoadSchedule(member.userId, member.roleSlug, routeId);
    };

    const handleDashboardNavigate = () => {
        navigate(`/team/${member.userId}`);
    };

    const handleDeleteActiveSchedule = () => {
        dispatch(clearSVActiveSchedule());
    };

    const createCustomTaskForRoute = useCallback(async () => {
        dispatch(
            addModalToQueue({
                type: ModalType.CreateCustomTask,
                id: ModalType.CreateCustomTask,
                payload: {
                    agentId: member.userId,
                    images: [],
                },
            }),
        );
    }, [dispatch, member]);

    return (
        <StyledMemberCard isActive={isActive}>
            <FlexContainer justifyContent="flex-start" gap="12px">
                <StyledTeamBadge dot isWorking={member.isWorking}>
                    <Avatar size={SIZES.AVATAR} icon={<StyledTeamLogo />} />
                </StyledTeamBadge>

                <MemberInfo member={member} />
            </FlexContainer>

            <MemberActions
                isProcessing={isProcessing}
                hasSchedule={hasSchedule}
                canAccess={canAccess}
                onLoad={handleLoad}
                onNavigate={onNavigate}
                onDashboard={handleDashboardNavigate}
                onDelete={handleDeleteActiveSchedule}
                onCreateCustomTask={createCustomTaskForRoute}
            />
        </StyledMemberCard>
    );
};
